<template>
    <el-container style="height: calc(100vh - 80px)" class="bg-white">
        <el-header style="padding: 0px; height: auto; ">
            <!-- title部 -->
            <div class="h-12 p-4 shadow-md">ダッシュボード</div>

            <!-- 検索部 -->
            <div class="p-5">
                <div class="border border-gray-300 rounded-md p-4">
                    <el-form :model="searchForm" label-width="auto">
                        <div class="flex flex-col md:flex-row md:flex-wrap gap-x-4">
                            <el-form-item label="契約会社" class="md:w-80">
                                <CompanySelector v-model="searchForm.company_code" :isSearch=true
                                    :disabled="loginUserRole !== ROLES.SYSTEM" />
                            </el-form-item>

                            <el-form-item label="現場" class="md:w-80">
                                <SiteSelector :company_code="searchForm.company_code" :isSearch=true
                                    v-model="searchForm.site_code" />
                            </el-form-item>

                            <el-form-item label="作業状態" class="md:w-80">
                                <el-select v-model="isWorkStatus" placeholder="作業状態を選択してください"
                                    @change="handleWorkStatusChange">
                                    <el-option :value="ALL" label="すべて" />
                                    <el-option :value="true" label="作業中" />
                                    <el-option :value="false" label="作業完了" />
                                </el-select>
                            </el-form-item>

                            <el-form-item label="休憩状態" class="md:w-80">
                                <el-select v-model="isBreakStatus" placeholder="休憩状態を選択してください"
                                    :disabled="!isWorkStatus">
                                    <el-option :value="ALL" label="すべて" />
                                    <el-option :value="true" label="休憩中" />
                                    <el-option :value="false" label="休憩状態にはありません" />
                                </el-select>
                            </el-form-item>

                            <el-form-item label="デバイス状態" class="md:w-80">
                                <el-select v-model="appStatus" placeholder="デバイス状態を選択してください" :disabled="!isWorkStatus"
                                    @change="handleAppStatusChange">
                                    <el-option :value="ALL" label="すべて" />
                                    <el-option value="normal" label="正常" />
                                    <el-option value="not_comm" label="通信不能" />
                                    <el-option value="not_normal" label="計測不能" />
                                </el-select>
                            </el-form-item>

                            <el-form-item label="HSI" class="md:w-80">
                                <el-select v-model="zoneStatus" multiple placeholder="HSIを選択してください"
                                    :disabled="!isWorkStatus || appStatus === 'not_comm' || appStatus === 'not_normal'">
                                    <template v-for="item in zones">
                                        <el-option v-if="appStatus !== 'normal' || item.value !== 0" :key="item.value"
                                            :label="item.label" :value="item.value"
                                            :disabled="zoneStatus.length === 1 && zoneStatus.includes(item.value)">
                                            <div class="flex items-center">
                                                <el-tag :color="item.color" style="margin-right: 8px" size="small" />
                                                <span :style="{ color: item.color }">{{ item.label }}</span>
                                            </div>
                                        </el-option>
                                    </template>
                                    <template #tag>
                                        <el-tag v-for="value in zoneStatus" :key="value"
                                            :color="zones.find(zone => zone.value === value)?.color" />
                                    </template>
                                </el-select>
                            </el-form-item>

                            <el-button class="w-24" type="primary" @click="handleSearchButton">検索</el-button>
                        </div>
                    </el-form>
                </div>
            </div>

            <div class="flex flex-row items-end pb-2">
                <span class="text-xs text-gray-500 flex-grow" :class="{
                    'pl-5': alarmMessage.length > 0,
                    'px-5': alarmMessage.length === 0
                }">
                    最大100現場表示します
                </span>
                <span v-if="alarmMessage.length > 0" class="text-xs text-gray-500 pr-5"
                    style="width: 280px;">最大50件表示します</span>
            </div>
        </el-header>

        <!-- 主显示部 -->
        <el-container v-loading="loading" style="height: 0px">
            <!-- 操作员状态部 -->
            <el-main style="padding-block: 0px;">
                <div v-for="item in mTableData" :key="item.site.site_code" class="mb-8">
                    <!-- 现场名 -->
                    <div class="text-lg font-bold text-sm">{{ item.site.site_name }}</div>

                    <div class="flex flex-wrap gap-4">
                        <!-- 作業中のみ表示 -->
                        <OperatorStatus v-for="operator in item.operators"
                            :key="item.site.site_code + '_' + operator.operator.operator_code" :data="operator"
                            @refreshOperatorStatus="handleRefreshOperatorStatus" />
                    </div>
                </div>
            </el-main>
            <!-- 右侧信息部 -->
            <el-aside v-if="alarmMessage.length > 0" width="280px" class="pr-5">
                <div v-for="(alarm, index) in alarmMessage" :key="index" class="mb-4"
                    :class="getHSIBackgroundColor(alarm.heat_strain_index)">
                    <div class="text-sm">
                        {{ formattedDate(alarm.check_timestamp) }}
                    </div>
                    <div class="text-sm">
                        {{ alarm.site_name }}
                    </div>
                    <div class="text-sm">
                        <a :href="'#' + alarm.site_code + '_' + alarm.operator_code" class="underline text-blue-500">
                            {{ alarm.operator_name }}
                        </a>さんが{{ getHSIStatus(alarm.heat_strain_index) }}体温に達しました。
                    </div>
                </div>
            </el-aside>
        </el-container>

        <el-footer style="height: 20px;"> </el-footer>
    </el-container>
</template>

<script lang="ts">
import axios, { AxiosResponse } from 'axios';
import { ElMessage } from 'element-plus';
import { defineComponent, onMounted, ref, onUnmounted } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { ErrorCode, ResponseData, ResponseStatus } from '@/components/common/response';
import { getErrorMessage } from '@/components/common/errorMessage';
import CompanySelector from '@/components/common/company-selector.vue';
import SiteSelector from '@/components/common/site-selector.vue';
import OperatorStatus from '@/components/common/operator-status.vue';
import { authService } from '@/services/authService';
import moment from 'moment';
import { ALL, ROLES } from '@/config/constants';
import { DashboardInfo } from '@/components/models/dashboardinfo';

export interface AlarmMessage {
    site_code: string,
    site_name: string,
    operator_code: string,
    operator_name: string,
    heat_strain_index: string,
    check_timestamp: string,
}

export default defineComponent({
    name: 'UserDashboard',
    components: {
        CompanySelector,
        SiteSelector,
        OperatorStatus,
    },
    setup() {
        const loginUserRole = authService.getUserInfo().user_role ?? '';
        const loginUserCompanyCode = authService.getUserInfo().company_code ?? '';

        const intervalId = ref<number>(0);
        const updateTrigger = ref(Date.now());  // 添加更新触发器

        const alarmMessage = ref<AlarmMessage[]>([]);

        // 搜索条件
        const searchForm = ref<any>({
            company_code: loginUserRole === ROLES.SYSTEM ? ALL : loginUserCompanyCode,
            site_code: ALL,
        });

        const isWorkStatus = ref<boolean | 'ALL'>(true);
        const isBreakStatus = ref<boolean | 'ALL'>(ALL);
        const appStatus = ref<'ALL' | 'normal' | 'not_comm' | 'not_normal'>(ALL);
        const zoneStatus = ref<number[]>([0, 1, 2, 3, 4]);

        const zones = ref([
            { color: '#d1d5db', label: '通信不能/計測不能', value: 0 },
            { color: '#14b8a6', label: '安全', value: 1 },
            { color: '#facc15', label: '中負荷', value: 2 },
            { color: '#f59e0b', label: '高負荷', value: 3 },
            { color: '#f87171', label: '危険', value: 4 },
        ]);

        const loading = ref<boolean>(true);
        const mTableData = ref<DashboardInfo[]>([]);

        const dialogFormVisible = ref<boolean>(false);

        const formRef = ref<FormInstance>()

        const rules = ref<FormRules>({
            operator_code: [
                { required: true, message: '作業員コードを入力してください', trigger: 'blur' },
                { min: 1, max: 20, message: '作業員コードは1〜20文字の間で入力してください', trigger: 'blur' }
            ],
            operator_name: [
                { required: true, message: '作業員名を入力してください', trigger: 'blur' },
                { min: 2, max: 25, message: '作業員名は2〜25文字の間で入力してください', trigger: 'blur' }
            ]
        });

        /***** マウント **************************/
        onMounted(() => {
            loading.value = true;

            Promise.all([
                getAlarmMessageList(),
                getOperatorList(),
            ]).catch((error) => {
                ElMessage.error(getErrorMessage(error as Error));
            }).finally(() => {
                loading.value = false;
            });

            intervalId.value = setInterval(() => {
                Promise.all([
                    getOperatorList(),
                    getAlarmMessageList(),
                ]).catch((error) => {
                    ElMessage.error(getErrorMessage(error as Error));
                });
            }, 20000);
        });

        onUnmounted(() => {
            if (intervalId.value) {
                clearInterval(intervalId.value);
            }
        });

        /***** ボタン **************************/
        const handleSearchButton = async () => {
            loading.value = true;

            try {
                await Promise.all([
                    getAlarmMessageList(),
                    getOperatorList(),
                ]);
            } catch (error) {
                ElMessage.error(getErrorMessage(error as Error));
            } finally {
                loading.value = false;
            }
        };

        const handleRestButton = () => {
        };

        const handleEndRestButton = () => {
        };

        const handleRefreshOperatorStatus = () => {
            getOperatorList();
        };

        const formattedDate = (check_timestamp: string) => {
            const date = new Date(check_timestamp);
            return moment(date.toLocaleString()).format('YYYY/MM/DD HH:mm:ss')
        };

        const getHSIStatus = (heatStrainIndexStr: string) => {
            const heatStrainIndex = Number(heatStrainIndexStr);
            if (heatStrainIndex < 1) {
                return '正常';
            } else if (heatStrainIndex < 3) {
                return '正常';
            } else if (heatStrainIndex < 7) {
                return '熱中症警戒';
            }
            return '熱中症危険';
        };

        const getHSIBackgroundColor = (heatStrainIndexStr: string) => {
            const heatStrainIndex = Number(heatStrainIndexStr);
            if (heatStrainIndex < 1) {
                return '';
            } else if (heatStrainIndex < 3) {
                return '';
            } else if (heatStrainIndex < 7) {
                return '';
            }
            return 'bg-red-200';
        };

        /***** DB 操作 **************************/
        const getOperatorList = async () => {
            const params: any = {
                company_code: searchForm.value.company_code === ALL ? '' : searchForm.value.company_code,
                site_code: searchForm.value.site_code === ALL ? '' : searchForm.value.site_code,
                is_work: isWorkStatus.value === ALL ? null : isWorkStatus.value,
                is_break: isBreakStatus.value === ALL ? null : isBreakStatus.value,
                app_status: appStatus.value,
                zone_status: zoneStatus.value,
            };

            const res: AxiosResponse<ResponseData<DashboardInfo[]>> = await axios.get('/api/operators2/search', { params });
            if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                const { data } = res.data;

                mTableData.value = data!;

                updateTrigger.value = Date.now();  // 更新触发器
            } else {
                ElMessage.error(res.data.message);
            }

        };

        const getAlarmMessageList = async () => {
            const params: any = {
                company_code: loginUserRole === ROLES.SYSTEM ? '' : loginUserCompanyCode,
            };

            const res: AxiosResponse<ResponseData<AlarmMessage[]>> = await axios.get('/api/operator/hsi_status', { params });
            if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                const { data } = res.data;

                alarmMessage.value = data ?? [];
            } else {
                ElMessage.error(res.data.message);
            }

        };

        const handleWorkStatusChange = (value: boolean | 'ALL') => {
            if (!value) {
                // 休憩状態
                isBreakStatus.value = ALL;
                // 设备状態
                appStatus.value = ALL;
                // HSI
                zoneStatus.value = [0];
            } else {
                zoneStatus.value = [0, 1, 2, 3, 4];
            }

            if (value === ALL) {
                zones.value[0].label = '通信不能/計測不能/作業完了';
            } else {
                zones.value[0].label = '通信不能/計測不能';
            }
        };

        const handleAppStatusChange = (value: 'ALL' | 'normal' | 'not_comm' | 'not_normal') => {
            if (value === 'not_comm' || value === 'not_normal') {
                zoneStatus.value = [0];
            } else if (value === 'normal') {
                zoneStatus.value = [1, 2, 3, 4];
            } else {
                zoneStatus.value = [0, 1, 2, 3, 4];
            }
        };


        return {
            ALL,
            searchForm,
            isWorkStatus,
            handleWorkStatusChange,
            isBreakStatus,
            appStatus,
            handleAppStatusChange,
            zoneStatus,
            zones,
            updateTrigger,
            dialogFormVisible,
            loading,
            mTableData,
            handleSearchButton,
            handleRestButton,
            handleEndRestButton,
            handleRefreshOperatorStatus,
            formattedDate,
            getHSIStatus,
            getHSIBackgroundColor,
            formRef,
            rules,
            loginUserRole,
            alarmMessage,
            ROLES,
        }
    }
});
</script>

<style scoped>
.el-tag {
    border: none;
    aspect-ratio: 1;
}
</style>